import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { DataRow } from './DataRow';

import { useSelector } from 'react-redux';
import { selectClient } from '@client/model';
import { usePolicyInsightContext } from './PolicyInsight';

import { possessive, formatInteger, FundingPolicyType, checkPolicyCalculationTypeEquals } from '@shared';
import { formatCurrencyValue } from './policyInsightUtils';

import { type PhasePayoutData } from './policyInsightUtils';

interface PhaseOverviewProps {
  locationOfCare: string;
  phaseDuration: string;
  totalPhaseCost: number;
  avgMonthlyCost: number;
  avgDailyCost: number;
}

function PhaseOverview({
  locationOfCare,
  phaseDuration,
  totalPhaseCost,
  avgMonthlyCost,
  avgDailyCost,
}: PhaseOverviewProps) {
  return (
    <div className="space-y-4 rounded-xl bg-gray-100/70 p-4">
      <DataRow label="Location of Care">{locationOfCare}</DataRow>
      <hr className="border-gray-200" />
      <DataRow label="Phase Duration">{phaseDuration}</DataRow>
      <hr className="border-gray-200" />
      <DataRow label="Total Phase Cost">{formatCurrencyValue(totalPhaseCost)}</DataRow>
      <hr className="border-gray-200" />
      <DataRow label="Avg Monthly Cost">{formatCurrencyValue(avgMonthlyCost)}</DataRow>
      <hr className="border-gray-200" />
      <DataRow label="Avg Daily Cost">{formatCurrencyValue(avgDailyCost)}</DataRow>
    </div>
  );
}

interface PolicyBenefitProps {
  policyType: FundingPolicyType | null;
  initialMaxBenefitAmount: number | null;
  policyContinuationBenefitAmount: number | null;
  inflationProtection: string | null;
  inflatedBenefitAmount: number | null;
  policyBenefitAvailable: number;
  inflationExplanation?: string;
}

function PolicyBenefit({
  policyType,
  initialMaxBenefitAmount,
  policyContinuationBenefitAmount,
  inflationProtection,
  inflatedBenefitAmount,
  policyBenefitAvailable,
  inflationExplanation,
}: PolicyBenefitProps) {
  // Determine label based on policy type (matching legacy logic)
  const isHybridWithCOB =
    checkPolicyCalculationTypeEquals(policyType, [
      FundingPolicyType.hybridLifeInsurance,
      FundingPolicyType.hybridLifeIra,
    ]) && policyContinuationBenefitAmount;

  const isLifeInsuranceWithRider = checkPolicyCalculationTypeEquals(
    policyType,
    FundingPolicyType.lifeInsuranceWithRider,
  );

  return (
    <div className="mt-4 p-4">
      <h4 className="mb-4 text-base font-medium text-gray-950">
        Policy Benefit available for this phase
      </h4>
      <div className="space-y-4">
        {isHybridWithCOB ? (
          <>
            <DataRow label="Death benefit amount">
              {formatCurrencyValue(initialMaxBenefitAmount)}
            </DataRow>
            <hr className="border-gray-200" />
            <DataRow label="Continuation of benefits amount">
              {formatCurrencyValue(policyContinuationBenefitAmount)}
            </DataRow>
          </>
        ) : isLifeInsuranceWithRider ? (
          <DataRow label="Initial death benefit amount">
            {formatCurrencyValue(initialMaxBenefitAmount)}
          </DataRow>
        ) : (
          <DataRow label="Initial max benefit amount">
            {formatCurrencyValue(initialMaxBenefitAmount)}
          </DataRow>
        )}
        {inflationProtection && (
          <>
            <hr className="border-gray-200" />
            <DataRow label="Inflation protection">{inflationProtection}</DataRow>
          </>
        )}
        {inflatedBenefitAmount !== null && (
          <>
            <hr className="border-gray-200" />
            <DataRow label="Inflated benefit amount">
              {formatCurrencyValue(inflatedBenefitAmount)}
            </DataRow>
          </>
        )}
        <hr className="border-gray-200" />
        <DataRow
          label="Policy benefit available / remaining for phase"
          className="font-medium"
        >
          {formatCurrencyValue(policyBenefitAvailable)}
        </DataRow>
      </div>
      {inflationExplanation && (
        <p className="mt-4 rounded-xl bg-gray-100/70 p-3 text-sm italic text-gray-700">
          {inflationExplanation}
        </p>
      )}
    </div>
  );
}

interface EligibleCareDaysProps {
  adlClaimEligibilityRequired: boolean;
  estimatedDaysInPhase: number;
  eligibleDaysWithTwoAdls: number | null;
  waitingPeriod: {
    remainingDays: number;
    totalDays: number | null;
  } | null;
  eligibleDaysAfterWaiting: number;
  calculationExplanation?: string;
  clientFirstName: string;
}

function EligibleCareDays({
  adlClaimEligibilityRequired,
  estimatedDaysInPhase,
  eligibleDaysWithTwoAdls,
  waitingPeriod,
  eligibleDaysAfterWaiting,
  calculationExplanation,
  clientFirstName,
}: EligibleCareDaysProps) {
  return (
    <div className="mt-4 p-4">
      <h4 className="mb-4 text-base font-medium text-gray-950">Eligible care days</h4>
      <div className="space-y-4">
        {adlClaimEligibilityRequired && (
          <>
            <p className="text-sm font-medium text-gray-950">2 ADL claim eligibility requirement</p>
            <DataRow label="Estimated days in Early Care Phase">{`${formatInteger(estimatedDaysInPhase)} days`}</DataRow>
            {eligibleDaysWithTwoAdls !== null && (
              <>
                <hr className="border-gray-200" />
                <DataRow label="Eligible days associated with 2-ADLs">{`${formatInteger(eligibleDaysWithTwoAdls)} days`}</DataRow>
                <p className="mt-4 rounded-xl bg-gray-100/70 p-3 text-sm italic text-gray-700">
                  Most long-term care insurance policies <strong>won’t be claim-eligible</strong>{' '}
                  until the policyholder is receiving help with at least{' '}
                  <strong>2 Activities of Daily Living (ADLs).</strong> We refer to these ADLs as
                  self-care activities earlier in the Personalize Plan section. Early Care Phase is
                  a prediction for when <strong>{clientFirstName}</strong> will need help with 1-2
                  ADLs. We assume the first half of this care phase is spent receiving help with 1
                  ADL (and is not covered by the policy).
                </p>
              </>
            )}
          </>
        )}
        {waitingPeriod && (
          <div className="space-y-4 pt-4">
            <p className="text-sm font-medium text-gray-950">Waiting period</p>
            <DataRow label="Remaining home care waiting period">{`${formatInteger(waitingPeriod.remainingDays)} (out of ${formatInteger(waitingPeriod.totalDays ?? 0)}) days`}</DataRow>
          </div>
        )}
        <hr className="border-gray-200" />
        <DataRow label="Eligible days after waiting period">{`${formatInteger(eligibleDaysAfterWaiting)} days`}</DataRow>
      </div>
      {calculationExplanation && (
        <p className="mt-4 rounded-xl bg-gray-100/70 p-3 text-sm italic text-gray-700">
          {calculationExplanation}
        </p>
      )}
    </div>
  );
}

interface DailyMaxBenefitProps {
  dailyMaxBenefitForCare: number | null;
  inflationProtection: string | null;
  inflatedDailyMaxBenefit: number | null;
  expectedDailyPayout: number;
  isReimbursementPolicy: boolean;
  avgDailyCost: number;
  clientFirstName: string;
}

function DailyMaxBenefit({
  dailyMaxBenefitForCare,
  inflationProtection,
  inflatedDailyMaxBenefit,
  expectedDailyPayout,
  isReimbursementPolicy,
  avgDailyCost,
  clientFirstName,
}: DailyMaxBenefitProps) {
  if (!dailyMaxBenefitForCare) return null;

  return (
    <div className="mt-4 p-4">
      <h4 className="mb-4 text-base font-medium text-gray-950">Daily maximum benefit</h4>
      <div className="space-y-4">
        <DataRow label="Daily max benefit for care at home">
          {formatCurrencyValue(dailyMaxBenefitForCare)}
        </DataRow>
        {inflationProtection && (
          <>
            <hr className="border-gray-200" />
            <DataRow label="Inflation protection">{inflationProtection}</DataRow>
          </>
        )}
        {inflatedDailyMaxBenefit !== null && (
          <>
            <hr className="border-gray-200" />
            <DataRow label="Inflated daily max benefit">
              {formatCurrencyValue(inflatedDailyMaxBenefit)}
            </DataRow>
          </>
        )}
        {isReimbursementPolicy &&
          inflatedDailyMaxBenefit &&
          inflatedDailyMaxBenefit > avgDailyCost && (
            <>
              <hr className="border-gray-200" />
              <DataRow label="Expected daily payout">
                {formatCurrencyValue(expectedDailyPayout)}
              </DataRow>
              <p className="mt-4 rounded-xl bg-gray-100/70 p-3 text-sm italic text-gray-700">
                The daily max benefit is more than the expected average daily cost of care{' '}
                <strong>({formatCurrencyValue(avgDailyCost)})</strong> for{' '}
                <strong>{clientFirstName}</strong>, so a <strong>reimbursement</strong> policy will
                only pay up to the actual cost incurred.
              </p>
            </>
          )}
      </div>
    </div>
  );
}

interface CostCoverageProps {
  remainingBenefitAmount: number;
  eligibleCareCost: number;
  eligibleDays: number;
  avgDailyCost: number;
  totalCostCoverage: number;
  remainingBenefitAfterPhase: number;
  phaseLabel: string;
}

function CostCoverage({
  remainingBenefitAmount,
  eligibleCareCost,
  eligibleDays,
  avgDailyCost,
  totalCostCoverage,
  remainingBenefitAfterPhase,
  phaseLabel,
}: CostCoverageProps) {
  return (
    <div className="mt-4 p-4">
      <h4 className="mb-4 text-base font-medium text-gray-950">
        Cost coverage for {phaseLabel} Phase and remaining benefit
      </h4>
      <div className="space-y-4">
        <DataRow label="Remaining benefit amount">
          {formatCurrencyValue(remainingBenefitAmount)}
        </DataRow>
        <hr className="border-gray-200" />
        <DataRow label="Eligible care cost">{formatCurrencyValue(eligibleCareCost)}</DataRow>
      </div>
      <p className="my-4 rounded-xl bg-gray-100/70 p-3 text-sm italic text-gray-700">
        This amount is calculated by multiplying eligible days by the average daily cost of care for
        this phase <strong>{formatInteger(eligibleDays)}</strong> days x{' '}
        <strong>{formatCurrencyValue(avgDailyCost)}</strong>
      </p>
      <hr className="border-gray-200" />
      <div className="mt-4 space-y-4">
        <DataRow
          highlight="green"
          label="Total cost coverage"
        >
          {formatCurrencyValue(totalCostCoverage)}
        </DataRow>
        <hr className="border-gray-200" />
        <DataRow label={`Remaining benefit amount (After reducing by the ${phaseLabel} payout)`}>
          {formatCurrencyValue(remainingBenefitAfterPhase)}
        </DataRow>
      </div>
    </div>
  );
}

interface PhasePayoutAccordionProps {
  phaseData: PhasePayoutData;
  phaseNumber: number;
  clientFirstName: string;
  policyPremiumStartYear: number | null;
  phaseEndYear: number | null;
  inflationPeriodYears: number;
}

function PhasePayoutAccordion({
  phaseData,
  phaseNumber,
  clientFirstName,
  policyPremiumStartYear,
  phaseEndYear,
  inflationPeriodYears,
}: PhasePayoutAccordionProps) {
  const inflationExplanation =
    phaseData.inflationProtection && policyPremiumStartYear
      ? `Inflation rate(s) is applied to the policy's benefit pool(s) from the year the policy was purchased (${policyPremiumStartYear}) to the year ${possessive(clientFirstName)} ${phaseData.phaseLabel} is expected to end (${phaseEndYear ? Math.round(phaseEndYear) : ''}), for a total of ${inflationPeriodYears.toFixed(2)} years.`
      : undefined;

  const applicableEligibleDays =
    phaseData.eligibleDaysWithTwoAdls ?? phaseData.estimatedDaysInPhase;
  const waitingPeriodExplanation = phaseData.waitingPeriod
    ? `This is calculated by subtracting eligible days in the care phase by the remaining waiting period days (${formatInteger(applicableEligibleDays)} days - ${formatInteger(phaseData.waitingPeriod.remainingDays)} days).`
    : undefined;

  return (
    <AccordionItem
      value={`phase-${phaseNumber}`}
      className="rounded-xl border border-gray-200"
    >
      <AccordionTrigger
        className="p-4"
        withBackground={false}
      >
        <span className="text-left font-medium text-gray-950">
          {phaseNumber}. Payout for the {phaseData.phaseLabel} Phase
        </span>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4 pt-0">
        <PhaseOverview
          locationOfCare={phaseData.locationOfCare}
          phaseDuration={phaseData.phaseDuration}
          totalPhaseCost={phaseData.totalPhaseCost}
          avgMonthlyCost={phaseData.avgMonthlyCost}
          avgDailyCost={phaseData.avgDailyCost}
        />

        <PolicyBenefit
          policyType={phaseData.policyType}
          initialMaxBenefitAmount={phaseData.initialMaxBenefitAmount}
          policyContinuationBenefitAmount={phaseData.policyContinuationBenefitAmount}
          inflationProtection={phaseData.inflationProtection}
          inflatedBenefitAmount={phaseData.inflatedBenefitAmount}
          policyBenefitAvailable={phaseData.policyBenefitAvailable}
          inflationExplanation={inflationExplanation}
        />

        <EligibleCareDays
          adlClaimEligibilityRequired={phaseData.adlClaimEligibilityRequired}
          estimatedDaysInPhase={phaseData.estimatedDaysInPhase}
          eligibleDaysWithTwoAdls={phaseData.eligibleDaysWithTwoAdls}
          waitingPeriod={phaseData.waitingPeriod}
          eligibleDaysAfterWaiting={phaseData.eligibleDaysAfterWaiting}
          calculationExplanation={waitingPeriodExplanation}
          clientFirstName={clientFirstName}
        />

        <DailyMaxBenefit
          dailyMaxBenefitForCare={phaseData.dailyMaxBenefitForCare}
          inflationProtection={phaseData.inflationProtection}
          inflatedDailyMaxBenefit={phaseData.inflatedDailyMaxBenefit}
          expectedDailyPayout={phaseData.expectedDailyPayout}
          isReimbursementPolicy={phaseData.isReimbursementPolicy}
          avgDailyCost={phaseData.avgDailyCost}
          clientFirstName={clientFirstName}
        />

        <CostCoverage
          remainingBenefitAmount={phaseData.remainingBenefitAmount}
          eligibleCareCost={phaseData.eligibleCareCost}
          eligibleDays={phaseData.eligibleDaysAfterWaiting}
          avgDailyCost={phaseData.avgDailyCost}
          totalCostCoverage={phaseData.totalCostCoverage}
          remainingBenefitAfterPhase={phaseData.remainingBenefitAfterPhase}
          phaseLabel={phaseData.phaseLabel}
        />
      </AccordionContent>
    </AccordionItem>
  );
}

export function PolicyPayoutCalculation() {
  const client = useSelector(selectClient);
  const { insightData, fundingSourceId } = usePolicyInsightContext();

  const {
    clientFirstName,
    policyPhaseCalculations,
    multipleFundingSources,
    clientPhasePredictedEndYears,
  } = client;

  const phases = insightData.phases;
  const phaseCalculations = policyPhaseCalculations[fundingSourceId];
  const ltcPolicy = multipleFundingSources.ltcPolicy[fundingSourceId];
  const policyPremiumStartYear = ltcPolicy.policyPremiumStartYear;

  return (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-gray-950">Policy Payout Calculation</h2>
      <Accordion
        type="single"
        defaultValue="phase-1"
        collapsible
        className="space-y-3"
      >
        {phases.map((phaseData, index) => (
          <PhasePayoutAccordion
            key={phaseData.phase}
            phaseData={phaseData}
            phaseNumber={index + 1}
            clientFirstName={clientFirstName}
            policyPremiumStartYear={policyPremiumStartYear}
            phaseEndYear={clientPhasePredictedEndYears[phaseData.phase]}
            inflationPeriodYears={phaseCalculations[index]?.inflationPeriodYears ?? 0}
          />
        ))}
      </Accordion>
    </section>
  );
}
